import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { message } from 'antd';

import { Button, Loading, QuickEdit } from '../../../../../components';
import {
  FIND_COMMUNICATION_PLAN_BY_PLAN,
  UPDATE_COMMUNICATION_PLAN,
} from '../../../queries';

import { lastUpdateDateFormatter } from '../../../../../utils';
import AntModal from '../../../../../components/generic/AntModal';
import StandaloneForm from '../../../../../components/generic/Form/StandaloneForm';
import { ModifyVariablesArgs } from '../../../../../components/generic/Table/TableV3';
import QueryErrorBoundary from '../../../../../components/error-handling';
import { formatContactInfo } from '../../../../../utils/formatUserData';
import { useUserContext } from '../../../../../context/PermissionsContext';

export type FindCommunicationPlanByPlan = {
  communicationPlanInPlace: boolean;
  email: string;
  lastUpdate: string;
  planOwnerFirstName: string;
  planOwnerLastName: string;
  uuid: string;
};

interface DataReturned {
  findCommunicationPlanByPlan: FindCommunicationPlanByPlan;
}

interface CommunicationSectionProps {
  readonly planId: string;
}

export function CommunicationSection({ planId }: CommunicationSectionProps) {
  const [item, setItem] = useState<FindCommunicationPlanByPlan | null>();
  const [modalOpen, setModalOpen] = useState(false);

  const { isAdmin, canEdit } = useUserContext();
  const [messageApi, contextHolder] = message.useMessage();

  const { data, loading, error } = useQuery<DataReturned>(
    FIND_COMMUNICATION_PLAN_BY_PLAN,
    {
      variables: { uuid: planId },
      skip: !planId,
    },
  );

  const [updateGQL] = useMutation(UPDATE_COMMUNICATION_PLAN, {
    refetchQueries: [FIND_COMMUNICATION_PLAN_BY_PLAN],
  });

  const contactInfo = formatContactInfo({
    firstName: data?.findCommunicationPlanByPlan?.planOwnerFirstName.trim(),
    lastName: data?.findCommunicationPlanByPlan?.planOwnerLastName.trim(),
    email: data?.findCommunicationPlanByPlan?.email.trim(),
  });

  const handleClick = (record: FindCommunicationPlanByPlan) => {
    setModalOpen(true);
    setItem(record);
  };

  const onCancel = () => {
    setModalOpen(false);
  };

  const onFinalize = () => {
    setModalOpen(false);
    setItem(null);
  };

  const fields = [
    {
      key: 'communicationPlanInPlace',
      label: 'Communication Plan In Place?',
      type: 'select',
      options: [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ],
      isRequired: true,
    },
    { key: 'autocomplete', type: 'autocomplete' },
    {
      key: 'lastUpdate',
      label: 'Last Update',
      type: 'date',
      isRequired: true,
    },
  ];

  const addVariables = ({ values }: ModifyVariablesArgs) => {
    const {
      sectionOwnerfirstName: planOwnerFirstName,
      sectionOwnerlastName: planOwnerLastName,
      communicationPlanInPlace,
      ...rest
    } = values;

    return {
      ...rest,
      planOwnerFirstName,
      planOwnerLastName,
      communicationPlanInPlace: communicationPlanInPlace === 'true',
    };
  };

  if (loading) return <Loading />;

  return (
    <QueryErrorBoundary error={error}>
      <div className="flex flex-col flex-nowrap justify-center items-center text-primary-200   font-normal text-sm tracking-wider relative">
        {contextHolder}
        {(isAdmin || canEdit) && (
          <Button
            type="edit"
            onClick={() => handleClick(data?.findCommunicationPlanByPlan!)}
            className="absolute right-4 top-2"
          >
            <QuickEdit />
          </Button>
        )}
        <div className="inline-flex flex-row">
          <div className="inline-flex flex-col text-right">
            <div className="my-2 p-2 border-r">
              <h6 className="font-semibold text-primary-100 text-[14px]">
                Crisis Communication Plan In Place?
              </h6>
            </div>
            <div className="my-2 p-2 border-r">
              <h6 className="font-semibold text-primary-100 text-[14px]">
                Plan Owner
              </h6>
            </div>
            <div className="my-2 p-2 border-r">
              <h6 className="font-semibold text-primary-100 text-[14px]">
                Last Plan Update
              </h6>
            </div>
          </div>
          <div className="inline-flex flex-col">
            <h6 className="my-2 p-2">
              {data?.findCommunicationPlanByPlan.communicationPlanInPlace
                ? 'Yes'
                : 'No'}
            </h6>
            <h6 className="my-2 p-2">
              {contactInfo ? (
                <>{contactInfo}</>
              ) : (
                <i className="placeholder-text">
                  No communication plan owner information provided
                </i>
              )}
            </h6>
            <h6 className="my-2 p-2">
              {lastUpdateDateFormatter(
                data?.findCommunicationPlanByPlan.lastUpdate!,
              )}
            </h6>
          </div>
        </div>
        <AntModal
          openModal={modalOpen}
          onCancel={onCancel}
          title="Crisis Communication Plan"
        >
          <StandaloneForm
            fields={fields}
            currentRecord={item}
            canDelete={false}
            updateGQL={updateGQL}
            modifyVariables={addVariables}
            onFinalize={onFinalize}
          />
        </AntModal>
      </div>
    </QueryErrorBoundary>
  );
}
